import { log } from 'log';
import { prop } from 'ramda';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { useGetURLTemplates } from 'js/hooks/useGetURLTemplates';
import { userSetAttributesAction } from 'js/hooks/user/store/actions';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useLoginByCredentials } from 'js/hooks/auth/hooks/useLoginByCredentials';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as helpers from './helpers';
import * as constants from './constants';
import * as actions from '../../store/actions';
import { IBankIDAuthFormValues } from '../../types';
import { useDynamicReducerDispatch } from '../../store';
import { useDeviceSelector } from 'js/store/hooks';
import { useIsMounted } from 'js/hooks/useIsMounted';

export const useOnSubmit = (replacerType: string) => {
    const isMounted = useIsMounted();
    const reduxDispatch = useDispatch();
    const getUrlTemplates = useGetURLTemplates();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const loginByCredentials = useLoginByCredentials();
    const getErrorMessage = useGetErrorMessage();
    const translate = useComponentTranslations();
    const dispatch = useDynamicReducerDispatch();

    return useCallback(async (values: IBankIDAuthFormValues) => {
        // removing old form error message;
        dispatch(actions.setFormError(undefined));

        try {
            // Start initializing of IMS external auth script with personalId
            log('pasSend', { request: values, actionName: 'ExternalAuth', description: 'initExternalAuth' });

            const externalAuthResponse = await helpers.initExternalAuth(values.personalId);

            if (externalAuthResponse.errorCode) {
                log('pasError', { response: externalAuthResponse, actionName: 'ExternalAuth', description: 'initExternalAuth' });

                if (externalAuthResponse.errorCode === 301) {
                    isMounted() && dispatch(actions.setIsRecaptchaShown(true));

                    return '';
                }

                isMounted() && dispatch(actions.setFormError(
                    getErrorMessage({ data: externalAuthResponse }, externalAuthResponse.playerMessage)
                ));

                window.externalAuth?.cancel();

                return;
            }

            log('pasReceived', { response: externalAuthResponse, actionName: 'ExternalAuth', description: 'initExternalAuth' });
            // end section

            // show preloader screen
            isMounted() && dispatch(actions.setIsWaitingForBankIdConfirmation(true));

            // Trying to open bankID app on mobile by link configured within IMS templates
            if (!isDesktop) {
                const urlTemplatesResponse = await getUrlTemplates({ urlTypeList: [constants.BANK_ID_APP_URL_TEMPLATE] });

                if (!urlTemplatesResponse.data?.urlList?.length) {
                    if (isMounted()) {
                        dispatch(actions.setIsWaitingForBankIdConfirmation(false));
                        dispatch(actions.setFormError(translate('playtech.mobile.system.bankIDAuth.urlTemplate.error')));
                    }

                    window.externalAuth?.cancel();
                }

                helpers.redirectToBankIDUrl(urlTemplatesResponse.data.urlList[0].url, externalAuthResponse.autostartToken);
            }

            // Start Poling with IMS in order to wait until user logins within a BankID app
            log('pasSend', { request: { actionToken: externalAuthResponse.actionToken }, actionName: 'ExternalAuth', description: 'startPolling' });

            const loginToBankIDResponse = await helpers.loginToBankID(externalAuthResponse.actionToken);

            if (loginToBankIDResponse.errorCode) {
                isMounted() && dispatch(actions.setFormError(getErrorMessage({ data: externalAuthResponse }, translate('playtech.mobile.system.bankIDAuth.urlTemplate.error'))));

                log('pasError', { response: loginToBankIDResponse, actionName: 'ExternalAuth', description: 'startPolling' });

                isMounted() && dispatch(actions.setIsWaitingForBankIdConfirmation(false));

                window.externalAuth?.cancel();

                return;
            }

            // no need to register, player is already present
            if (loginToBankIDResponse.result?.userId && replacerType === 'registration') {
                if (isMounted()) {
                    dispatch(actions.setFormError(translate('playtech.mobile.system.bankIDAuth.userAlreadyExists')));
                    dispatch(actions.setIsWaitingForBankIdConfirmation(false));
                }

                window.externalAuth?.cancel();

                return;
            }

            log('pasReceived', { response: loginToBankIDResponse, actionName: 'ExternalAuth', description: 'startPolling' });
            // end section

            // Log in to IMS and OpenAPI with external token
            if (replacerType === 'login') {
                try {
                    await loginByCredentials(
                        values.personalId,
                        loginToBankIDResponse.result?.externalToken,
                        false,
                        false,
                        'externalToken'
                    );
                } catch (error: any) {
                    if (error?.data?.errorCode === 301 && isMounted()) {
                        dispatch(actions.setIsRecaptchaShown(true));
                        dispatch(actions.setIsWaitingForBankIdConfirmation(false));

                        return '';
                    }

                    if (error?.data?.sessionValidationData) {
                        dispatch(actions.setIsWaitingForBankIdConfirmation(false));

                        return '';
                    }

                    if (isMounted()) {
                        dispatch(actions.setFormError(getErrorMessage(error)));
                        dispatch(actions.setIsWaitingForBankIdConfirmation(false));

                        window.externalAuth?.cancel();
                    }

                    return;
                }
            } else {
                // saving data to be autofilled within registration form
                reduxDispatch(userSetAttributesAction({
                    registrationInfo: {
                        playerHash: loginToBankIDResponse.result?.playerHash,
                        externalToken: loginToBankIDResponse.result?.externalToken,
                        birthdate: loginToBankIDResponse.result.birthDate || '',
                        firstname: loginToBankIDResponse.result.firstName || '',
                        lastname: loginToBankIDResponse.result.lastName || '',
                        personalId: loginToBankIDResponse.result.personalId || '',
                        address: loginToBankIDResponse.result.address || '',
                        address2: loginToBankIDResponse.result.address2 || '',
                        zip: loginToBankIDResponse.result.postalCode || '',
                        city: loginToBankIDResponse.result.city || '',
                        state: loginToBankIDResponse.result.municipality || '',
                        province: loginToBankIDResponse.result.province || '',
                        userName: loginToBankIDResponse.result.personalId || ''
                    }
                }));

                isMounted() && dispatch(actions.setIsWaitingForBankIdConfirmation(false));

                publicEventsAPIService.trigger('bankID-auth-register-success');

                window.externalAuth?.cancel();
            }
        } catch (error) {
            if (typeof error === 'string') {
                isMounted() && dispatch(actions.setFormError(translate(error)));
            } else {
                isMounted() && dispatch(actions.setFormError(getErrorMessage({ data: error })));
            }

            isMounted() && dispatch(actions.setIsWaitingForBankIdConfirmation(false));

            window.externalAuth?.cancel();
        }
    }, [
        dispatch,
        getErrorMessage,
        getUrlTemplates,
        isDesktop,
        isMounted,
        loginByCredentials,
        reduxDispatch,
        replacerType,
        translate
    ]);
};
